import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { NAV_LINKS, COMPANY } from '@/lib/data';
import Logo from './Logo';

export const Nav = ({ transparent = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // solid: light cream nav (scrolled state, or non-home pages)
  // transparent: over hero (Home only, before scroll)
  const solid = !transparent || scrolled;

  return (
    <>
      {/* Utility bar (top) */}
      <div
        className={`hidden md:block text-[11px] tracking-[0.24em] uppercase transition-colors duration-500 border-b ${
          solid ? 'bg-ink-900 text-ivory-100 border-ink-900' : 'bg-transparent text-ivory-100 border-transparent'
        }`}
      >
        <div className="container-x flex items-center justify-between py-2">
          <div className="flex items-center gap-5 opacity-80">
            <span>{COMPANY.rc}</span>
            <span className="text-gold">/</span>
            <span>Maitama 2, FCT Abuja</span>
          </div>
          <div className="flex items-center gap-5 opacity-95">
            <a href={`mailto:${COMPANY.email}`} className="hover:text-gold transition-colors" data-testid="topbar-email">{COMPANY.email}</a>
            <span className="text-gold">/</span>
            <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="hover:text-gold transition-colors flex items-center gap-2" data-testid="topbar-phone">
              <Phone className="w-3 h-3" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          solid
            ? 'bg-ivory-50/95 backdrop-blur-xl border-b border-gold/20 shadow-[0_2px_20px_rgba(11,11,12,0.06)]'
            : 'bg-gradient-to-b from-ink-900/60 via-ink-900/30 to-transparent border-b border-transparent'
        }`}
        data-testid="site-header"
      >
        <div className="container-x flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="nav-home-logo" className="shrink-0">
            {/* On solid light nav, use native (light) logo. On transparent-over-hero, use plated dark variant. */}
            <Logo variant={solid ? 'light' : 'dark'} />
          </Link>

          <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[11px] tracking-[0.22em] uppercase font-medium transition-colors duration-300 ${
                    isActive
                      ? 'text-gold-600'
                      : solid
                      ? 'text-ink-800 hover:text-gold-600'
                      : 'text-white hover:text-gold [text-shadow:0_1px_8px_rgba(0,0,0,0.6)]'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link to="/contact" className="hidden md:inline-flex btn-gold text-[10px] px-5 py-2.5" data-testid="nav-cta-enquiry">
              Enquire
            </Link>
            <button
              type="button"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className={`lg:hidden w-10 h-10 grid place-items-center border transition-colors ${
                solid ? 'text-ink-900 border-ink-900/20 hover:border-gold' : 'text-ivory-50 border-white/20 hover:border-gold'
              }`}
              data-testid="nav-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer — solid cream on light nav, ink-900 on transparent nav */}
        <div
          className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${open ? 'max-h-[85vh] opacity-100' : 'max-h-0 opacity-0'}`}
          data-testid="mobile-nav"
        >
          <div className={`${solid ? 'bg-ivory-50 border-t border-gold/20' : 'bg-ink-900 border-t border-white/10'} py-4 shadow-2xl`}>
            <div className="container-x flex flex-col divide-y divide-gold/10">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === '/'}
                  onClick={() => setOpen(false)}
                  data-testid={`mnav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3.5 text-[12px] tracking-[0.24em] uppercase font-medium ${
                      isActive ? 'text-gold-600' : solid ? 'text-ink-800' : 'text-ivory-100'
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" onClick={() => setOpen(false)} className="btn-gold mt-5 w-full justify-center" data-testid="mnav-cta">
                Enquire Now
              </Link>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Nav;
